/**
 * Serviço específico para dados do Dashboard
 * Fornece métodos para buscar estatísticas e dados do sistema educacional
 */

import { apiGet, fetchAllPaginated, fetchSchoolsSimple } from './api';
import { STATUS_ALUNO, ehAlunoMatriculado, normalizarStatusAluno, rotuloStatusAluno } from '../utils/statusAluno';

/**
 * Busca todas as cidades disponíveis
 */
export const getAllCities = async () => {
  return fetchAllPaginated('/cities/');
};

/**
 * Busca todos os estudantes (com paginação completa)
 */
export const getAllStudents = async (cityId = null) => {
  const params = cityId ? { city: cityId } : {};
  return fetchAllPaginated('/students/', params);
};

export const getStudentsPaginated = async (page = 1, pageSize = 10, cityId = null) => {
  const params = { page, page_size: pageSize };
  if (cityId) params.city = cityId;
  return apiGet('/students/', params);
};

export const getAllClasses = async (cityId = null) => {
  const params = cityId ? { city: cityId } : {};
  return fetchAllPaginated('/classes/', params);
};

export const getAllTeachers = async (cityId = null) => {
  const params = cityId ? { city: cityId } : {};
  return fetchAllPaginated('/teachers/', params);
};

export const getAllSchools = async (cityId = null) => {
  const params = cityId ? { city: cityId } : {};
  return fetchAllPaginated('/schools/', params);
};

export const getSchoolsSimple = async ({ todasAsCidades = false } = {}) => {
  return fetchSchoolsSimple(null, '', todasAsCidades);
};

export const getSchoolIdebIndicators = async (fiscalYear = null, cityId = null) => {
  const params = {};
  if (fiscalYear) params.fiscal_year = fiscalYear;
  if (cityId) params.city = cityId;
  return fetchAllPaginated('/school-ideb-indicators/', params);
};

export const getSchoolFlowIndicators = async (fiscalYear = null, cityId = null) => {
  const params = {};
  if (fiscalYear) params.fiscal_year = fiscalYear;
  if (cityId) params.city = cityId;
  return fetchAllPaginated('/school-flow-indicators/', params);
};

export const getAlerts = async (resolved = false, cityId = null) => {
  const params = { resolvido: resolved };
  if (cityId) params.city = cityId;
  return fetchAllPaginated('/alerts/', params);
};

export const getSchoolGeolocations = async (cityId = null) => {
  const params = cityId ? { city: cityId } : {};
  return fetchAllPaginated('/school-geolocation/', params);
};

export const getClassAttendanceSummary = async (yearMonth = null) => {
  const params = yearMonth ? { year_month: yearMonth } : {};
  return fetchAllPaginated('/class-attendance-summary/', params);
};

export const getMunicipalFinancial = async (fiscalYear = null) => {
  const params = fiscalYear ? { fiscal_year: fiscalYear } : {};
  return apiGet('/municipal-financial/', params);
};

export const getDashboardSecretaria = async () => {
  try {
    return await apiGet('/dashboard-secretaria/');
  } catch (error) {
    console.warn('Endpoint dashboard-secretaria nao disponivel, usando dados agregados');
    return null;
  }
};

export const getSimuladosAnalytics = async ({ cityId = null, schoolId = null, classId = null, grade = null } = {}) => {
  const params = {};
  if (cityId) params.city = cityId;
  if (schoolId) params.school = schoolId;
  if (classId) params.class = classId;
  if (grade) params.grade = grade;
  return apiGet('/dashboard-simulados/', params);
};

export const getAllSubjects = async () => {
  return fetchAllPaginated('/subjects/');
};

/**
 * Painel da Rede (coordenação) — agrega o diário: frequência, notas, aulas,
 * rankings. O escopo de turmas segue o papel do usuário no backend.
 */
export const getPainelRede = async ({ schoolYear = null, idSchool = null, idClass = null, shift = null, grade = null, city = null } = {}) => {
  const params = {};
  if (schoolYear) params.school_year = schoolYear;
  if (idSchool) params.id_school = idSchool;
  if (idClass) params.id_class = idClass;
  if (shift) params.shift = shift;
  if (grade) params.grade = grade;
  if (city) params.city = city;
  return apiGet('/dashboard-diario/rede/', params);
};

/**
 * Meu Painel (professor) — cockpit de uma turma: pendências, frequência,
 * notas e alunos em atenção. Sem idClass, o backend usa a única turma do
 * professor ou devolve a lista para o seletor (needs_selection).
 */
export const getPainelProfessor = async ({ idClass = null, schoolYear = null } = {}) => {
  const params = {};
  if (idClass) params.id_class = idClass;
  if (schoolYear) params.school_year = schoolYear;
  return apiGet('/dashboard-diario/professor/', params);
};

/**
 * Busca todos os dados do dashboard de uma vez, ja filtrados por cidade.
 */
export const fetchDashboardData = async (cityId = null) => {
  const [
    students,
    classes,
    teachers,
    schools,
    idebIndicators,
    flowIndicators,
    alerts,
    geolocations,
  ] = await Promise.all([
    getAllStudents(cityId),
    getAllClasses(cityId),
    getAllTeachers(cityId).catch(() => getAllTeachers()),
    getAllSchools(cityId),
    getSchoolIdebIndicators(null, cityId).catch(() => []),
    getSchoolFlowIndicators(null, cityId).catch(() => []),
    getAlerts(false, cityId).catch(() => []),
    getSchoolGeolocations(cityId).catch(() => []),
  ]);

  // Se o backend nao suportar city em /teachers/, filtramos client-side usando
  // o conjunto de escolas da cidade.
  let filteredTeachers = teachers;
  if (cityId && Array.isArray(schools) && schools.length) {
    const schoolIds = new Set(schools.map((s) => s.id));
    const byRelation = teachers.filter((t) => {
      if (t.id_school && schoolIds.has(t.id_school)) return true;
      if (Array.isArray(t.schools) && t.schools.some((id) => schoolIds.has(id))) return true;
      if (Array.isArray(t.school_ids) && t.school_ids.some((id) => schoolIds.has(id))) return true;
      return false;
    });
    // So aplica filtro client-side se resultou em algum vinculo; caso contrario
    // mantem o que o backend ja retornou (evita zerar por falta de dado).
    if (byRelation.length > 0) filteredTeachers = byRelation;
  }

  return {
    students,
    classes,
    teachers: filteredTeachers,
    schools,
    idebIndicators,
    flowIndicators,
    alerts,
    geolocations,
  };
};

export const calculateDashboardStats = (data) => {
  const { students, classes, teachers, schools, idebIndicators, flowIndicators, geolocations = [] } = data;

  const activeStudents = students.filter(s => ehAlunoMatriculado(s.status)).length;

  const studentsByClass = classes.map(cls => ({
    id: cls.id,
    name: cls.class_name || 'Sem nome',
    students: students.filter(s => s.id_class === cls.id).length,
    grade: cls.grade,
    shift: cls.shift,
    school_year: cls.school_year,
  }));

  const studentsBySchool = schools.map(school => {
    const schoolClasses = classes.filter(cls => cls.id_school === school.id);
    const schoolStudents = students.filter(s =>
      schoolClasses.some(cls => cls.id === s.id_class)
    ).length;
    return {
      id: school.id,
      name: school.school,
      students: schoolStudents,
    };
  });

  // Um item por status conhecido, contando pelo valor canônico — a base tem
  // 'ativo' e 'Ativo' de importações antigas junto com 'enrolled'.
  const studentsByStatus = STATUS_ALUNO.map(({ value }) => ({
    name: rotuloStatusAluno(value),
    value: students.filter(s => normalizarStatusAluno(s.status) === value).length,
  })).filter(item => item.value > 0);

  const classesByGrade = classes.reduce((acc, cls) => {
    const grade = cls.grade || 'Nao definido';
    const existing = acc.find(item => item.name === grade);
    if (existing) existing.count += 1;
    else acc.push({ name: grade, count: 1 });
    return acc;
  }, []).sort((a, b) => a.name.localeCompare(b.name));

  const geoMap = new Map(geolocations.map(g => [g.id_school, g.zona]));
  const urbanas = schools.filter(s => geoMap.get(s.id) === 'urbana').length;
  const rurais = schools.filter(s => geoMap.get(s.id) === 'rural').length;
  const semDados = schools.length - urbanas - rurais;

  const schoolsByZone = [
    { name: 'Urbana', value: urbanas },
    { name: 'Rural', value: rurais },
    ...(semDados > 0 ? [{ name: 'Sem dados', value: semDados }] : []),
  ].filter(item => item.value > 0);

  const idebAvg = idebIndicators.length > 0
    ? idebIndicators.reduce((sum, ind) => sum + parseFloat(ind.actual_avg_score || 0), 0) / idebIndicators.length
    : null;

  const avgApprovalRate = flowIndicators.length > 0
    ? flowIndicators.reduce((sum, ind) => sum + parseFloat(ind.taxa_aprovacao || 0), 0) / flowIndicators.length
    : null;

  const schoolsAboveTarget = idebIndicators.filter(ind =>
    parseFloat(ind.actual_avg_score || 0) >= parseFloat(ind.ideb_target || 0)
  ).length;
  const schoolsBelowTarget = idebIndicators.filter(ind =>
    parseFloat(ind.actual_avg_score || 0) < parseFloat(ind.ideb_target || 0)
  ).length;

  return {
    totals: {
      students: students.length,
      activeStudents,
      classes: classes.length,
      teachers: teachers.length,
      schools: schools.length,
    },
    ideb: { average: idebAvg, aboveTarget: schoolsAboveTarget, belowTarget: schoolsBelowTarget },
    approvalRate: avgApprovalRate,
    charts: { studentsByClass, studentsBySchool, studentsByStatus, classesByGrade, schoolsByZone },
  };
};

export default {
  getAllCities,
  getAllStudents,
  getStudentsPaginated,
  getAllClasses,
  getAllTeachers,
  getAllSchools,
  getSchoolsSimple,
  getSchoolIdebIndicators,
  getSchoolFlowIndicators,
  getAlerts,
  getSchoolGeolocations,
  getClassAttendanceSummary,
  getMunicipalFinancial,
  getDashboardSecretaria,
  getAllSubjects,
  fetchDashboardData,
  calculateDashboardStats,
  getSimuladosAnalytics,
  getPainelRede,
  getPainelProfessor,
};
